import { CallState } from 'matrix-js-sdk/lib/webrtc/call';
import { refreshTurnServersAndRestartIce } from './matrixCallIceUtils';

const DEFAULT_MAX_CALLS = 500;

const randomId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (_) {}
  return `${Date.now()}-${Math.random()}`;
};

class CallEngine {
  constructor({ client = null, onUpdate = null, maxCalls = DEFAULT_MAX_CALLS } = {}) {
    this.client = client;
    this.onUpdate = onUpdate;
    this.maxCalls = maxCalls;

    this.calls = new Map();
    this.activeCallId = null;
    this.incomingCallId = null;
  }

  emitUpdate() {
    try {
      this.onUpdate?.();
    } catch (_) {}
  }

  setClient(client) {
    this.client = client;
  }

  createCallEntry(call, direction = null) {
    const callId = call?.callId;
    if (!callId) return null;

    if (!this.calls.has(callId) && this.calls.size >= this.maxCalls) {
      const oldestKey = this.calls.keys().next().value;
      if (oldestKey != null) this.remove(oldestKey);
    }

    const existing = this.calls.get(callId);
    const entry = existing ?? {
      call,
      callId,
      direction,
      instanceId: randomId(),
      state: call?.state || 'idle',
      timers: new Map(),
      metrics: {
        startAt: Date.now(),
        connectedAt: null,
      },
      tracks: new Set(),
      iceRetries: 0,
    };

    entry.call = call;
    entry.direction = direction ?? entry.direction ?? null;
    entry.state = call?.state || entry.state || 'idle';
    this.calls.set(callId, entry);
    return entry;
  }

  get(callId) {
    if (!callId) return null;
    return this.calls.get(callId) ?? null;
  }

  getByCall(call) {
    return this.get(call?.callId);
  }

  getActive() {
    return this.get(this.activeCallId)?.call ?? null;
  }

  getIncoming() {
    return this.get(this.incomingCallId)?.call ?? null;
  }

  setActiveCall(call) {
    this.activeCallId = call?.callId ?? null;
    if (call?.callId) this.createCallEntry(call);
    this.emitUpdate();
  }

  setIncomingCall(call) {
    this.incomingCallId = call?.callId ?? null;
    if (call?.callId) this.createCallEntry(call);
    this.emitUpdate();
  }

  clearTimer(callId, key) {
    const entry = this.get(callId);
    if (!entry) return;
    const timerId = entry.timers.get(key);
    if (timerId) clearTimeout(timerId);
    entry.timers.delete(key);
  }

  clearTimers(callId) {
    const entry = this.get(callId);
    if (!entry) return;
    entry.timers.forEach((id) => {
      try {
        clearTimeout(id);
      } catch (_) {}
    });
    entry.timers.clear();
  }

  setTimer(callId, key, fn, delay) {
    const entry = this.get(callId);
    if (!entry) return;

    if (entry.timers.has(key)) {
      clearTimeout(entry.timers.get(key));
    }

    const expectedInstanceId = entry.instanceId;
    const timeoutId = setTimeout(() => {
      entry.timers.delete(key);
      const current = this.get(callId);
      if (!current || current.instanceId !== expectedInstanceId) return;
      fn(current.call, current);
    }, delay);

    entry.timers.set(key, timeoutId);
  }

  updateState(callId, state) {
    const entry = this.get(callId);
    if (!entry) return;
    entry.state = state;
    if (state === CallState.Connected && !entry.metrics.connectedAt) {
      entry.metrics.connectedAt = Date.now();
    }
  }

  getCallUIState(callId) {
    const entry = this.get(callId);
    if (!entry) return 'idle';
    const state = entry.call?.state || entry.state;
    if (state === CallState.Ringing || state === 'ringing') return 'ringing';
    if (state === CallState.Connecting || state === 'connecting') return 'connecting';
    if (state === CallState.Connected || state === 'connected') return 'connected';
    return 'ended';
  }

  isTrackInUse(track, excludingCallId = null) {
    for (const entry of this.calls.values()) {
      if (!entry || (excludingCallId && entry.callId === excludingCallId)) continue;
      const streams = [entry.call?.localUsermediaStream, entry.call?.remoteUsermediaStream].filter(Boolean);
      for (const stream of streams) {
        const tracks = stream?.getTracks?.() || [];
        if (tracks.includes(track)) return true;
      }
    }
    return false;
  }

  stopMedia(call, { excludeIfInUseByOthers = true } = {}) {
    if (!call) return;
    const callId = call?.callId ?? null;
    const streams = [call.localUsermediaStream, call.remoteUsermediaStream].filter(Boolean);

    streams.forEach((stream) => {
      const tracks = stream?.getTracks?.() || [];
      tracks.forEach((track) => {
        if (!track) return;
        if (excludeIfInUseByOthers && this.isTrackInUse(track, callId)) return;
        try {
          // Explicitly disable before stop to release mic/camera immediately.
          track.enabled = false;
        } catch (_) {}
        try {
          track.stop?.();
        } catch (_) {}
      });
    });
  }

  attemptIceRecovery(callId, { maxRetries = 5, watchdogMs = 32000, onTerminate } = {}) {
    const entry = this.get(callId);
    if (!entry || !entry.call) return;

    if (entry.iceRetries >= maxRetries) {
      onTerminate?.(entry.call, 'ice_failed', entry);
      return;
    }

    entry.iceRetries += 1;

    const run = async () => {
      let result;
      try {
        result = await refreshTurnServersAndRestartIce(entry.call, this.client);
      } catch (_) {
        onTerminate?.(entry.call, 'ice_failed', entry);
        return;
      }

      if (!result?.ok) {
        entry.iceRetries -= 1;
        // Only end the call when we actually pushed config / ICE restart and it cannot work.
        // Missing PC briefly is normal during setup — let the Matrix SDK continue negotiating.
        if (result?.reason === 'set_configuration_failed' || result?.reason === 'no_restart_ice') {
          onTerminate?.(entry.call, 'ice_failed', entry);
        }
        return;
      }

      this.setTimer(callId, 'ice_watchdog', (currentCall) => {
        if (!currentCall || currentCall.callHasEnded?.()) return;
        try {
          const ice = currentCall.peerConn?.iceConnectionState;
          if (ice === 'connected' || ice === 'completed') return;
          // Mid-call: SDK can stay "Connected" while ICE is dead — end only if ICE is clearly failed.
          if (currentCall.state === CallState.Connected) {
            if (ice === 'checking' || ice === 'connecting') return;
            if (ice === 'failed' || ice === 'closed') {
              onTerminate?.(currentCall, 'ice_failed', entry);
            }
            return;
          }
        } catch (_) {
          /* fall through */
        }
        onTerminate?.(currentCall, 'ice_failed', entry);
      }, watchdogMs);
    };

    void run().catch(() => {});
  }

  cleanup(callId) {
    const entry = this.get(callId);
    if (!entry) return;
    this.cleanupTimers(entry);
    this.cleanupMedia(entry);
    this.cleanupState(entry);
    this.calls.delete(callId);
  }

  cleanupTimers(entry) {
    entry?.timers?.forEach((id) => {
      try {
        clearTimeout(id);
      } catch (_) {}
    });
    entry?.timers?.clear?.();
  }

  cleanupMedia(entry) {
    if (!entry?.call) return;
    this.stopMedia(entry.call);
  }

  cleanupState(entry) {
    if (!entry) return;
    if (this.activeCallId === entry.callId) this.activeCallId = null;
    if (this.incomingCallId === entry.callId) this.incomingCallId = null;
  }

  remove(callId) {
    const entry = this.get(callId);
    if (!entry) return;
    this.cleanup(callId);
    this.emitUpdate();
  }

  clearAll() {
    for (const callId of Array.from(this.calls.keys())) {
      this.cleanup(callId);
    }
    this.calls.clear();
    this.activeCallId = null;
    this.incomingCallId = null;
    this.emitUpdate();
  }
}

export default CallEngine;
